<template>
  <div>
    <div
      class="bg-[#FAFAFA] flex flex-col overflow-hidden"
      :style="containerStyles"
    >
      <div class="w-full px-3 pt-5 tablet-h:pt-8">
        <div class="max-w-[1234px] mx-auto">
          <div class="flex justify-between mb-5">
            <div
              class="font-secondary text-[16px] tablet-h:text-[22px] text-gray-800"
            >
              <template v-if="firstLoad">Searching...</template>
              <template v-else>
                {{ result?.searchFiles.total }} results for "{{
                  route.query.query
                }}"
              </template>
            </div>
            <div class="hidden tablet-h:flex items-center mt-1">
              <Icon :size="18" name="Dacr" class="fill-gray-800 mr-2" />
              <div class="font-semibold font-primary text-[16px] text-gray-800">
                Dacr Copyright Protected Files
              </div>
            </div>
          </div>

          <div
            class="flex items-center pb-5 tablet-h:pb-8"
            :class="buttonContainerClasses"
          >
            <div class="hidden tablet-h:flex items-center">
              <Float.Base class="mr-5" anchor="left">
                <Float.Trigger>
                  <UiButton icon="Filter" :primary="!!numberOfSelectedFilters">
                    {{ filterString }}
                  </UiButton>
                </Float.Trigger>
                <Float.Component>
                  <div class="filter bg-white w-[190px] rounded-md">
                    <div
                      class="px-3 py-2 font-primary font-medium text-[14px] leading-[18px]"
                    >
                      Filter by
                    </div>
                    <div class="w-full h-[1px] bg-gray-800/10" />
                    <div class="px-3 py-2">
                      <div
                        v-for="category in filterCategories"
                        :key="category.value"
                        class="flex items-center mb-2.5"
                      >
                        <Checkbox v-model="filterBy[category.value]" :size="20">
                          <div
                            class="ml-2 font-primary text-[15px] leading-[18px] text-gray-800"
                          >
                            {{ category.label }}
                          </div>
                        </Checkbox>
                      </div>
                    </div>
                  </div>
                </Float.Component>
              </Float.Base>

              <Float.Base anchor="left">
                <Float.Trigger>
                  <UiButton icon="Sort"> Sort </UiButton>
                </Float.Trigger>
                <Float.Component>
                  <div class="filter bg-white w-[220px] rounded-md">
                    <div
                      class="px-3 py-2 font-primary font-medium text-[14px] leading-[18px]"
                    >
                      Sort by
                    </div>
                    <div class="w-full h-[1px] bg-gray-800/10" />
                    <div class="px-3 py-2">
                      <div
                        v-for="opt in sortOptions"
                        :key="opt.value"
                        class="flex items-center mb-2.5"
                      >
                        <Radio v-model="sortBy" :value="opt.value">
                          {{ opt.label }}
                        </Radio>
                      </div>
                    </div>
                  </div>
                </Float.Component>
              </Float.Base>
            </div>
            <div class="tablet-h:hidden flex items-center">
              <UiButton
                class="mr-2.5"
                icon="Filter"
                :primary="!!numberOfSelectedFilters"
                @click="handleFilterClick"
              >
                {{ filterString }}
              </UiButton>
              <UiButton icon="Sort" @click="handleSortClick"> Sort </UiButton>
            </div>
            <div
              v-if="numberOfSelectedFilters"
              class="hidden tablet-v:block font-primary text-green-900 leading-[18px] cursor-pointer ml-5"
              @click="handleResetFiltersClick"
            >
              Reset filters
            </div>
            <DacrBadge class="tablet-h:hidden w-[116px] ml-auto" />
          </div>
        </div>
      </div>

      <div id="container" class="overflow-y-auto w-full px-3 pb-8">
        <div
          class="max-w-[1234px] mx-auto border border-[#CCCCCC]/50 rounded-md bg-white"
          :class="contentContainerClasses"
        >
          <template v-if="files?.length">
            <PublicSearchItem
              v-for="file in files"
              :key="file.id"
              :file="file as SearchFile"
            />
          </template>
          <div v-else-if="showSkeleton">
            <div
              v-for="n in 4"
              :key="n"
              class="p-3 flex tablet-h:items-center border-b border-[#EFEFEF] last:border-none"
            >
              <Skeleton
                class="p-2 min-w-[70px] desktop:min-w-[210px] h-[65px] desktop:h-[118px] bg-[#FAFAFA] group-hover:bg-[#F5F5F5] rounded-md flex items-center justify-center mr-4 desktop:mr-5"
              />
              <div class="w-full">
                <Skeleton class="w-[170px] h-[18px] mb-4 desktop:mb-5" />
                <div
                  class="flex flex-col tablet-h:flex-row tablet-h:items-center"
                >
                  <div class="mb-4 desktop:mb-0 w-[200px] mr-3">
                    <Skeleton class="w-10 h-2.5 mb-1" />
                    <Skeleton class="w-[118px] h-3" />
                  </div>
                  <div class="mb-4 desktop:mb-0 w-[200px]">
                    <Skeleton class="w-10 h-2.5 mb-1" />
                    <Skeleton class="w-[118px] h-3" />
                  </div>
                  <div class="mb-4 desktop:mb-0 tablet-h:w-[330px] mr-2">
                    <Skeleton class="w-10 h-2.5 mb-1" />
                    <Skeleton class="w-3/4 h-3" />
                  </div>
                  <div>
                    <Skeleton class="w-10 h-2.5 mb-1" />
                    <Skeleton class="w-[118px] h-3" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            v-else
            class="w-full h-[400px] flex flex-col items-center justify-center"
          >
            <div
              class="size-[126px] flex items-center justify-center bg-gray-200/30 rounded-[16px] mb-[43px]"
            >
              <Lottie name="Search-Results" class="min-w-[160px]" />
            </div>

            <div
              class="font-semibold font-primary text-[20px] leading-[20px] tracking-[-0.25px] text-gray-800 mb-4"
            >
              No results found
            </div>

            <div
              class="font-secondary text-[14px] leading-[22px] text-gray-800 max-w-[302px] text-center"
            >
              Try different keywords
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  FileCategory,
  SearchSort,
  useSearchFilesQuery,
  type SearchFile,
} from '@/shared/graphql/legacy/generated'
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { UiButton, Checkbox, Icon, Skeleton, Float, Radio } from '@dacr/ui'
import PublicSearchItem from '@/features/search/components/PublicSearchItem.vue'
import { useDevice } from '@/shared/composables/useDevice'
import { useAvailableHeight } from '@/shared/composables/useAvailableHeight'
import DacrBadge from '@/assets/svg/other/DacrBadge.svg'
import { useHead } from '@unhead/vue'
import Lottie from '@/shared/components/Lottie.vue'
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'

const route = useRoute()

useHead({
  title: `Search results in Dacr public files for "${route.query.query}" - Dacr`,
})

const filterCategories = [
  { label: 'Images', value: FileCategory.Images },
  { label: 'Videos', value: FileCategory.Videos },
  { label: 'Audio', value: FileCategory.Audio },
  { label: 'Documents', value: FileCategory.Documents },
  { label: 'Code', value: FileCategory.Code },
  { label: 'Other', value: FileCategory.Other },
]

const sortOptions = [
  { label: 'Name (A-Z)', value: SearchSort.NameAsc },
  { label: 'Name (Z-A)', value: SearchSort.NameDesc },
  { label: 'Timestamp (Newest)', value: SearchSort.TimestampDesc },
  { label: 'Timestamp (Oldest)', value: SearchSort.TimestampAsc },
]

const filterBy = ref<Record<FileCategory, boolean>>({
  [FileCategory.Audio]: false,
  [FileCategory.Code]: false,
  [FileCategory.Other]: false,
  [FileCategory.Images]: false,
  [FileCategory.Videos]: false,
  [FileCategory.Documents]: false,
})

const sortBy = ref(SearchSort.TimestampDesc)

const variables = computed(() => ({
  query: route.query.query as string,
  filterBy: Object.keys(filterBy.value).filter(
    (key) => filterBy.value[key as FileCategory]
  ) as FileCategory[],
  sortBy: sortBy.value,
}))

const { result, fetchMore, loading } = useSearchFilesQuery(variables)

const device = useDevice()
const { availableHeight } = useAvailableHeight()
const dialog = useDialog()

const isScrolling = ref(false)

const files = computed(() => result.value?.searchFiles.files)
const showSkeleton = computed(() => loading.value && !files.value?.length)
const firstLoad = computed(() => loading.value && files.value === undefined)

const containerStyles = computed(() => ({
  height: `${availableHeight.value}px`,
}))

const buttonContainerClasses = computed(() => ({
  'border-b border-[#CCCCCC]/50': isScrolling.value,
}))

const contentContainerClasses = computed(() => ({
  'border-t-0 rounded-t-0': isScrolling.value,
}))

const numberOfSelectedFilters = computed(
  () => Object.values(filterBy.value).filter(Boolean).length
)

const filterString = computed(() => {
  let string = 'Filter'
  if (numberOfSelectedFilters.value) {
    string += ` (${numberOfSelectedFilters.value})`
  }
  return string
})

const fetchNextPage = () => {
  const nextValue = result.value?.searchFiles.next
  if (nextValue && !loading.value) {
    fetchMore({
      variables: { next: nextValue },
      updateQuery: (previousData, { fetchMoreResult: newData }) => {
        if (!newData) return previousData
        return {
          __typename: 'Query' as const,
          searchFiles: {
            __typename: 'SearchFilesResponse' as const,
            files: [
              ...previousData.searchFiles.files,
              ...newData.searchFiles.files,
            ],
            next: newData.searchFiles.next,
            total: newData.searchFiles.total,
          },
        }
      },
    })
  }
}

const handleResetFiltersClick = () => {
  Object.keys(filterBy.value).forEach((key) => {
    filterBy.value[key as FileCategory] = false
  })
}

const handleScroll = () => {
  const containerEl = document.querySelector('#container')
  if (!containerEl) return
  const scrollTop = containerEl.scrollTop
  const scrollPos = containerEl.clientHeight + scrollTop
  isScrolling.value = scrollTop > 0
  if (scrollPos / containerEl.scrollHeight >= 0.5) {
    fetchNextPage()
  }
}

const handleFilterClick = () => {
  dialog.open(Dialogs.SearchFilter, {
    props: {
      initialState: filterBy.value,
      onChange(data: typeof filterBy.value) {
        filterBy.value = data
      },
    },
  })
}

const handleSortClick = () => {
  dialog.open(Dialogs.SearchSort, {
    props: {
      initialState: sortBy.value,
      onChange(data: SearchSort) {
        sortBy.value = data
      },
    },
  })
}

onMounted(() => {
  document.querySelector('#container')?.addEventListener('scroll', handleScroll)
})
</script>

<style scoped>
.filter {
  box-shadow:
    0 0 8px 0 rgb(0 0 0 / 10%),
    0 2px 4px 0 rgb(0 0 0 / 20%);
}
</style>
